import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowUpRight, Trash, MapPin, Warning, Sparkle, CircleNotch, Scales,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { logDev } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useI18n } from "@/context/I18nContext";

// ---------------------------------------------------------------------------
// SessionDetail — shows one past discovery session together with the leads
// it discovered. Supports single- and multi-lead deletion.
// ---------------------------------------------------------------------------
export default function SessionDetail() {
  const { id } = useParams();
  const { locale } = useI18n();
  const t = (tr, en) => (locale === "en" ? en : tr);
  const nav = useNavigate();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // "single"|"bulk"|null
  const [singleTarget, setSingleTarget] = useState(null);
  const [eliminatedOpen, setEliminatedOpen] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [comparisonIds, setComparisonIds] = useState(() => new Set());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/history/sessions/${id}`);
      setSession(data);
    } catch (e) {
      toast.error(locale === "en" ? "Failed to load session" : "Oturum yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [id, locale]);

  useEffect(() => { load(); }, [load]);

  // If the session is still running (arrived from Search.js just now),
  // poll until it reaches a terminal state so results appear live.
  // The polling loop tolerates transient errors (network blip / 5xx) by
  // logging them in dev and continuing; the interval self-cancels on
  // unmount and on a terminal state.
  useEffect(() => {
    if (!session) return;
    if (["complete", "error"].includes(session.state)) return;
    let consecutiveFailures = 0;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/history/sessions/${id}`);
        consecutiveFailures = 0;
        setSession(data);
        if (["complete", "error"].includes(data.state)) {
          clearInterval(iv);
        }
      } catch (err) {
        consecutiveFailures += 1;
        logDev("sessionDetail.poll", err);
        // Give up after ~30s of solid failures (15 * 2s) so the tab does
        // not hammer a broken endpoint forever.
        if (consecutiveFailures >= 15) clearInterval(iv);
      }
    }, 2000);
    return () => clearInterval(iv);
  }, [session, id]);

  const leads = useMemo(() => session?.leads || [], [session]);
  const isSessionComplete = session?.state === "complete";
  const isSessionInProgress = Boolean(session) && !["complete", "error"].includes(session.state);
  const isSearchingForLeads = !loading && isSessionInProgress && leads.length === 0;
  const eliminatedCandidates = session?.eliminated_candidates || [];
  const eliminatedCount = session?.dropped_off_topic || 0;
  const comparedLeads = useMemo(() => leads.filter((lead) => comparisonIds.has(lead.id)), [leads, comparisonIds]);
  const allSelected = leads.length > 0 && selected.size === leads.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggle = (leadId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      return next;
    });
  };
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };

  const toggleComparison = (leadId) => {
    setComparisonIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else if (next.size >= 4) {
        toast.error(t("En fazla 4 firma karşılaştırabilirsiniz", "You can compare up to 4 companies"));
      } else {
        next.add(leadId);
      }
      return next;
    });
  };

  const openComparison = () => {
    if (comparedLeads.length < 2) return;
    nav(`/gecmis/${id}/karsilastir?ids=${encodeURIComponent(comparedLeads.map((lead) => lead.id).join(","))}`);
  };

  const runDelete = async () => {
    const mode = confirmDelete;
    setConfirmDelete(null);
    try {
      if (mode === "single" && singleTarget) {
        await api.delete(`/leads/${singleTarget}`);
        setSession((s) => ({ ...s, leads: s.leads.filter((l) => l.id !== singleTarget) }));
        setComparisonIds((prev) => new Set([...prev].filter((leadId) => leadId !== singleTarget)));
        setSingleTarget(null);
        toast.success(t("Kayıt silindi", "Lead deleted"));
      } else if (mode === "bulk") {
        const ids = Array.from(selected);
        const { data } = await api.post("/leads/bulk-delete", { ids });
        setSession((s) => ({ ...s, leads: s.leads.filter((l) => !selected.has(l.id)) }));
        setComparisonIds((prev) => new Set([...prev].filter((leadId) => !selected.has(leadId))));
        setSelected(new Set());
        toast.success(
          t(`${data.deleted || 0} kayıt silindi`, `${data.deleted || 0} lead(s) deleted`)
        );
      }
    } catch (e) {
      toast.error(t("Silinemedi", "Delete failed"));
    }
  };

  const restoreCandidate = async (candidateId) => {
    setRestoringId(candidateId);
    try {
      const { data } = await api.post(`/history/sessions/${id}/restore-candidate/${candidateId}`);
      setSession((s) => ({
        ...s,
        leads: [...(s.leads || []), data.lead],
        result_ids: data.result_ids,
        result_count: data.result_count,
        dropped_off_topic: data.dropped_off_topic,
        eliminated_candidates: (s.eliminated_candidates || []).filter((candidate) => candidate.id !== candidateId),
      }));
      setEliminatedOpen(false);
      toast.success(t("Aday sonuçlara geri eklendi", "Candidate restored to results"));
    } catch (e) {
      toast.error(t("Aday geri eklenemedi", "Could not restore candidate"));
    } finally {
      setRestoringId(null);
    }
  };

  const started = session ? new Date(session.started_at || Date.now()) : null;
  const dateFmt = started
    ? started.toLocaleString(locale === "en" ? "en-US" : "tr-TR", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "";

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-[1400px]">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => nav("/gecmis")} data-testid="back-to-history">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="acas-eyebrow mb-1">{t("Keşif oturumu", "Discovery session")}</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight truncate" data-testid="session-title">
            {loading ? <Skeleton className="h-8 w-96" /> : (session?.raw_goal || t("(başlıksız)", "(untitled)"))}
          </h1>
          {session && (
            <div className="text-xs text-muted-foreground mt-2 flex flex-wrap gap-x-4 gap-y-1">
              <span className="mono">{dateFmt}</span>
              {(leads.length > 0 || isSessionComplete) && (
                <span data-testid="session-result-count">{leads.length} {t("kayıt", "leads")}</span>
              )}
              {eliminatedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setEliminatedOpen(true)}
                  data-testid="eliminated-candidates-trigger"
                  className="text-left hover:text-primary transition-colors"
                >
                  {eliminatedCount} {t("aday elendi", "candidates filtered")}
                </button>
              )}
              {(session.plan?.lead_types || []).map((lt) => (
                <Badge key={lt} variant="outline" className="font-normal text-[10px]">{lt}</Badge>
              ))}
            </div>
          )}
          {session?.plan?.target_customer && (
            <div className="text-xs text-muted-foreground mt-2 flex items-start gap-1.5">
              <Sparkle size={11} weight="fill" className="text-primary mt-0.5" />
              <span>{session.plan.target_customer}</span>
            </div>
          )}
        </div>
      </div>

      {eliminatedOpen && (
        <Dialog open onOpenChange={setEliminatedOpen}>
          <DialogContent data-testid="eliminated-candidates-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("Elenen adaylar", "Filtered candidates")}</DialogTitle>
            <DialogDescription>{t("Adayları sonuçlara geri ekleyebilirsiniz.", "You can restore candidates to the results.")}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
            {eliminatedCandidates.map((candidate) => (
              <div key={candidate.id} data-testid={`eliminated-candidate-${candidate.id}`} className="rounded-md border border-border/60 bg-card p-3 flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="flex-1 min-w-0 space-y-2">
                  <div>
                    <div className="text-sm font-semibold truncate" data-testid={`eliminated-candidate-name-${candidate.id}`}>{candidate.company_name || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate" data-testid={`eliminated-candidate-location-${candidate.id}`}>{[candidate.industry, candidate.location].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  <div className="border-l-2 border-amber-500/70 pl-3 space-y-1.5" data-testid={`eliminated-explanation-${candidate.id}`}>
                    <div className="text-xs font-semibold text-foreground" data-testid={`eliminated-reason-${candidate.id}`}>
                      {candidate.elimination?.reason || t("Yetersiz veya belirsiz uygunluk kanıtı", "Insufficient or uncertain fit evidence")}
                    </div>
                    <div className="text-xs text-muted-foreground" data-testid={`eliminated-business-type-${candidate.id}`}>
                      {t("İşletme türü", "Business type")}: {candidate.elimination?.detected_business_type || "—"}
                      {candidate.elimination?.detected_sector ? ` · ${t("Sektör", "Sector")}: ${candidate.elimination.detected_sector}` : ""}
                    </div>
                    <p className="text-xs leading-relaxed text-muted-foreground" data-testid={`eliminated-target-explanation-${candidate.id}`}>
                      {candidate.elimination?.explanation || t(
                        "Bu aday için geçmiş oturumda ayrıntılı açıklama kaydedilmemiştir; mevcut kanıt hedef müşteri kriteriyle doğrulanmış uyum göstermiyor.",
                        "No detailed explanation was stored for this older session; the available evidence does not show verified alignment with the target-customer criteria."
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => restoreCandidate(candidate.id)}
                  disabled={restoringId === candidate.id}
                  data-testid={`restore-candidate-${candidate.id}`}
                >
                  {restoringId === candidate.id ? t("Ekleniyor…", "Restoring…") : t("Sonuçlara geri ekle", "Restore to results")}
                </Button>
              </div>
            ))}
            {eliminatedCandidates.length === 0 && (
              <div data-testid="eliminated-candidates-empty" className="py-6 text-center text-sm text-muted-foreground">
                {t("Bu oturumda görüntülenecek aday bulunmuyor.", "No candidates are available to show for this session.")}
              </div>
            )}
          </div>
          </DialogContent>
        </Dialog>
      )}

      {session?.summary && (
        <div className="rounded-lg border border-primary/25 bg-primary/[0.03] p-4 flex items-start gap-3" data-testid="session-summary">
          <Sparkle size={13} weight="fill" className="text-primary mt-0.5 flex-shrink-0" />
          <div className="text-sm leading-relaxed">{session.summary}</div>
        </div>
      )}

      {comparedLeads.length > 0 && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 flex flex-wrap items-center justify-between gap-3" data-testid="comparison-selection-bar">
          <div className="text-sm flex items-center gap-2">
            <Scales size={16} className="text-primary" weight="duotone" />
            <strong data-testid="comparison-selected-count">{comparedLeads.length}</strong>
            <span>{t("firma karşılaştırma için seçildi", "companies selected for comparison")}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setComparisonIds(new Set())} data-testid="comparison-clear-selection">
              {t("Temizle", "Clear")}
            </Button>
            <Button size="sm" onClick={openComparison} disabled={comparedLeads.length < 2} data-testid="open-company-comparison" className="gap-1.5">
              <Scales size={14} weight="duotone" /> {t("Karşılaştır", "Compare")}
            </Button>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 flex items-center justify-between" data-testid="bulk-action-bar">
          <div className="text-sm">
            <strong>{selected.size}</strong> {t("kayıt seçildi", "selected")}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              {t("Seçimi kaldır", "Clear selection")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmDelete("bulk")}
              data-testid="bulk-delete-btn"
              className="gap-1.5"
            >
              <Trash size={14} />
              {t("Seçilenleri sil", "Delete selected")}
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-border/60 bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-8">
                <Checkbox
                  checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                  onCheckedChange={toggleAll}
                  data-testid="lead-select-all"
                  disabled={leads.length === 0}
                />
              </TableHead>
              <TableHead>{t("Firma", "Company")}</TableHead>
              <TableHead>{t("Ticari rol", "Commercial role")}</TableHead>
              <TableHead>{t("Konum", "Location")}</TableHead>
              <TableHead>{t("Uyum", "Match")}</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && [1, 2, 3].map((i) => (
              <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
            ))}
            {isSearchingForLeads && (
              <TableRow data-testid="session-searching-state">
                <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <CircleNotch size={20} className="animate-spin text-primary" aria-hidden="true" />
                    <p className="font-medium text-foreground" data-testid="session-searching-title">Next io fırsatları araştırıyor…</p>
                    <p className="max-w-md" data-testid="session-searching-description">
                      Uygun işletmeler ve potansiyel müşteriler aranıyor. Sonuçlar hazır olduğunda burada görünecek.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!loading && isSessionComplete && leads.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-sm text-muted-foreground" data-testid="session-empty-state">
                  {t("Bu oturumda kayıt bulunmuyor.", "No leads in this session.")}
                </TableCell>
              </TableRow>
            )}
            {!loading && leads.map((lead) => (
              <TableRow
                key={lead.id}
                data-testid={`lead-row-${lead.id}`}
                className={selected.has(lead.id) ? "bg-primary/[0.03]" : ""}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selected.has(lead.id)}
                    onCheckedChange={() => toggle(lead.id)}
                    data-testid={`lead-select-${lead.id}`}
                  />
                </TableCell>
                <TableCell className="max-w-[320px] cursor-pointer" onClick={() => nav(`/musteriler/${lead.id}?session_id=${session.id}`)}>
                  <div className="font-semibold truncate">{lead.company_name}</div>
                  <div className="text-[11px] text-muted-foreground mono truncate">
                    {lead.website || lead.address || ""}
                  </div>
                </TableCell>
                <TableCell>
                  {lead.qualification?.relationship_type && (
                    <Badge variant="outline" className="font-normal text-[10px]" data-testid={`commercial-role-${lead.id}`}>
                      {lead.qualification.relationship_type === "reseller_sales_point" ? "Satış noktası / bayi"
                        : lead.qualification.relationship_type === "sponsor_prospect" ? "Sponsor adayı"
                        : lead.qualification.relationship_type === "event_participant_prospect" ? "Etkinlik katılımcısı adayı"
                        : lead.qualification.relationship_type === "personal_b2c_service_provider" ? "Kişisel hizmet sağlayıcısı"
                        : lead.qualification.relationship_type === "audience_access_channel" ? "Hedef kitle erişim kanalı"
                        : "B2B satış adayı"}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                    <MapPin size={12} />{lead.location || "—"}
                  </span>
                </TableCell>
                <TableCell>
                  <Badge variant={lead.qualification?.evidence_level === "strong" ? "default" : "outline"} data-testid={`qualification-level-${lead.id}`}>
                    {lead.qualification?.evidence_level === "strong" ? "Güçlü kanıt" : "Kısmi kanıt"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-0.5">
                    <button
                      type="button"
                      onClick={() => toggleComparison(lead.id)}
                      data-testid={`compare-toggle-${lead.id}`}
                      aria-pressed={comparisonIds.has(lead.id)}
                      aria-label={t("Karşılaştırma için seç", "Select for comparison")}
                      title={t("Karşılaştırma için seç", "Select for comparison")}
                      className={`p-1.5 rounded ${comparisonIds.has(lead.id) ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"}`}
                    >
                      <Scales size={14} weight={comparisonIds.has(lead.id) ? "fill" : "regular"} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSingleTarget(lead.id); setConfirmDelete("single"); }}
                      data-testid={`lead-delete-${lead.id}`}
                      aria-label={t("Sil", "Delete")}
                      className="p-1.5 rounded hover:bg-rose-500/10 text-muted-foreground hover:text-rose-600"
                    >
                      <Trash size={14} />
                    </button>
                    <button
                      onClick={() => nav(`/musteriler/${lead.id}`)}
                      data-testid={`lead-open-${lead.id}`}
                      className="p-1.5 rounded hover:bg-accent"
                    >
                      <ArrowUpRight size={14} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => { if (!o) { setConfirmDelete(null); setSingleTarget(null); } }}>
        <AlertDialogContent data-testid="delete-lead-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Warning size={18} weight="fill" className="text-rose-500" />
              {confirmDelete === "bulk"
                ? t(`${selected.size} kayıt silinsin mi?`, `Delete ${selected.size} lead(s)?`)
                : t("Bu kaydı sil?", "Delete this lead?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "Bu işlem geri alınamaz. Kayıt, tüm keşif oturumlarından da temizlenir.",
                "This cannot be undone. The lead is also removed from every discovery session."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-lead-cancel">
              {t("Vazgeç", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={runDelete}
              data-testid="delete-lead-confirm"
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-500"
            >
              {t("Sil", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
